import { asArray } from '../array.js';
import { makeSimpleDomRect } from '../dom.js';
import { clamp } from '../math.js';
import { mergeIgnoringNullish } from '../object.js';
import { resolveMargin, positionGeneric, resolveElement, resolvePositionArea, resolveMarginAxis, getViewportRect, flipPositionArea, fitsInViewport, computeViewportOverflow } from './positioner.utils.js';
import { VirtualRect } from './positioner.virtual-rect.js';

/**
 * A builder for setting up a chain of positioning steps to compute an element's position relative
 * to another element. Size and scroll may also be adjusted as part of the positioning process.
 *
 * A Positioner's first step should always be a `position()` call to set the initial position
 * relative to the anchor element. The remaining calls typically adjust relative to this starting
 * position.
 *
 * Think of this like svg path commands, where the first step is always a "move to".
 *
 * ## Glossary
 *
 * - **popup**: The element being positioned.
 * - **anchor**: The element the popup is positioned relative to.
 */
class Positioner {
    ctx;
    /**
     * Create a new Positioner instance.
     *
     * `viewportMargin` can be used to set up a margin around the viewport that will be respected
     * by the positioning process at every step. Think of it like insetting the viewport rectangle
     * that the positioner uses to keep elements on-screen.
     */
    static create({ viewportMargin = 0, debug = false } = {}) {
        return new Positioner({
            viewportMargin: resolveMargin(viewportMargin),
            positionArea: null,
            positionMargin: { x: 0, y: 0 },
            debug,
        });
    }
    /** Consumers should use `Positioner.create()` instead. */
    constructor(ctx) {
        this.ctx = ctx;
    }
    steps = [];
    /**
     * Position the popup relative to the anchor element.
     *
     * `area` is the only required param, and is based off of css [position-area].
     * `area` controls which side of the anchor the popup is positioned against.
     * The `span-*` values cause the popup to cover the anchor and extend out from the specified side.
     *
     * `margin` can be used to set the distance between the popup and anchor after positioning.
     * A margin of `0` means the popup will touch the anchor.
     *
     * [position-area]: https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Properties/position-area
     */
    position(args) {
        this.steps.push({ name: 'position', fn: stepPosition, args: resolvePositionArgs(args) });
        return this;
    }
    /**
     * Adjust the popup size so that it fits within the viewport. Ensures that the scrollable region
     * within the popup does not visually change by updating the scrollTop in regards to the new size.
     */
    clip(args = {}) {
        this.steps.push({ name: 'clip', fn: stepClip, args });
        return this;
    }
    /**
     * If the popup overflows the viewport, shift it back so it is fully visible.
     * Can be limited to a single axis.
     *
     * Typically used within `fallback` so it only runs when needed.
     */
    shift(axis) {
        this.steps.push({ name: 'shift', fn: stepShift, args: { axis } });
        return this;
    }
    /**
     * Flip the position area to the opposite side if the popup overflows the viewport.
     * Only flips in axes where overflow is present, or else limited by the `axis` param.
     *
     * Typically used within `fallback` so it only runs when needed.
     */
    flip(axis) {
        this.steps.push({ name: 'flip', fn: stepFlip, args: { axis } });
        return this;
    }
    /**
     * Try each positioning in order until the popup no longer overflows the viewport.
     *
     * Receives a function that is called with a helper to create a new Positioner instance with
     * the same base context. The entire chain of fallbacks may be limited to a single axis by
     * passing the `axis` param to the helper.
     *
     * Multiple Positioners may be returned, and the first that results in the popup fitting
     * within the viewport (along the configured `axis`) is used.
     *
     * @example
     *
     * ```ts
     * positioner
     *   .position({ area: 'bottom' })
     *   .fallback((p) => [
     *     p('y').flip(),
     *     p('x').flip(),
     *     p().flip(),
     *   ])
     * ```
     */
    fallback(fn) {
        this.steps.push({ name: 'fallback', fn: stepFallback, args: { fn } });
        return this;
    }
    /**
     * Run logic not pre-built by the Positioner. The provided function is called with
     * an object containing the `ctx` and `fns` helpers. The `fns` helpers are aware of
     * settings passed to the Positioner, such as `viewportMargin`.
     *
     * The `exec` function should modify the `ctx.target` directly as needed. Return `false`
     * to bail and reset the `target` to its original position before this step was run.
     */
    exec(fn) {
        this.steps.push({ name: 'exec', fn: stepExec, args: { fn } });
        return this;
    }
    /**
     * Not meant to be called by consumers. Runs the positioning steps and returns
     * the final context. `ctx.target` contains the final positioned rectangle.
     */
    run(anchorRect, targetRect, { ownerWindow = window, targetScale = 1, subRun = false, positionOverride, } = {}) {
        const ctx = {
            ownerWindow,
            anchor: anchorRect,
            target: new VirtualRect(targetRect),
            targetStart: targetRect,
            targetScale,
            context: this.ctx,
        };
        // When repositioning a "sticky" popup, the caller passes the area we previously landed on.
        // Apply it to the initial `position` step only, then drop it, so the fallback chain still
        // flips off of that side if it no longer fits. Ignored for internal fallback sub-runs.
        let pendingOverride = subRun ? undefined : positionOverride;
        const ownerDocument = ownerWindow.document;
        let div = ownerDocument.getElementById('fpl-positioner-debug');
        if (this.ctx.debug) {
            if (!subRun) {
                div?.remove();
                div = ownerDocument.createElement('div');
                div.id = 'fpl-positioner-debug';
                div.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          width: 100dvw;
          height: 100dvh;
          pointer-events: none;
          z-index: 9999999;
        `;
                ownerDocument.body.appendChild(div);
            }
        }
        for (let i = 0; i < this.steps.length; ++i) {
            const step = this.steps[i];
            const overrideThisStep = pendingOverride && step.name === 'position';
            const oldTarget = new VirtualRect(ctx.target);
            const result = step.fn(overrideThisStep ? { ...step.args, area: pendingOverride } : step.args, ctx);
            if (overrideThisStep)
                pendingOverride = undefined;
            if (result === false) {
                ctx.target = oldTarget;
            }
            else if (result instanceof VirtualRect) {
                ctx.target = result;
            }
            if (this.ctx.debug) {
                const stepDiv = ownerDocument.createElement('div');
                stepDiv.style.cssText = `
        position: absolute;
        display: flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        top: ${ctx.target.top}px;
        left: ${ctx.target.left}px;
        width: ${ctx.target.width}px;
        height: ${ctx.target.height}px;
        outline: 2px dashed hsl(${(i * 137.5) % 360}, 100%, 50%);
        outline-offset: ${i * 2}px;
      `;
                stepDiv.innerText = `${subRun ? 'sub ' : ''}${i + 1}. ${step.name}`;
                div?.appendChild(stepDiv);
            }
        }
        return ctx;
    }
}
function stepClip({ minHeight, padding }, { anchor, target, context, ownerWindow }) {
    const overflow = target.clipRect(getViewportRect(context.viewportMargin, ownerWindow));
    // No overflow: popup fits completely.
    // Both overflow: let scroll container handle it.
    if (overflow.top === overflow.bottom)
        return true;
    // Truthy check is okay, if `minHeight` is 0 or not set then any height works.
    if (minHeight && target.height < minHeight)
        return false;
    if (padding) {
        // If the anchor is closer to the top than `padding`, bail
        if (overflow.top && anchor.top - target.top < padding)
            return false;
        // if the anchor is closer to the bottom than `padding`, bail
        if (overflow.bottom && target.bottom - anchor.bottom < padding)
            return false;
    }
    return true;
}
function stepShift({ axis: axisIn }, { target, context, ownerWindow }) {
    const axis = axisIn ?? context.axis;
    const viewport = getViewportRect(context.viewportMargin, ownerWindow);
    let { left, top } = target;
    if (axis !== 'y') {
        left =
            target.width > viewport.width
                ? viewport.left
                : clamp(left, viewport.left, viewport.right - target.width);
    }
    if (axis !== 'x') {
        top =
            target.height > viewport.height
                ? viewport.top
                : clamp(top, viewport.top, viewport.bottom - target.height);
    }
    target.left = left;
    target.top = top;
}
function stepFlip({ axis: axisIn }, ctx) {
    const axis = axisIn ?? ctx.context.axis;
    const { context: { positionArea, positionMargin }, } = ctx;
    // Can't flip if we haven't positioned yet
    if (!positionArea)
        return;
    const flippedPositionArea = flipPositionArea(positionArea, axis);
    stepPosition({ area: flippedPositionArea, margin: positionMargin, axis }, ctx);
}
function stepFallback({ fn }, { anchor, target, targetScale, context, ownerWindow }) {
    const fallbacks = asArray(fn((axis) => new Positioner(mergeIgnoringNullish(context, { axis }))));
    {
        const axis = fallbacks[0]?.ctx.axis;
        if (fitsInViewport(target, context.viewportMargin, axis, ownerWindow))
            return;
    }
    for (const fallback of fallbacks) {
        const axis = fallback.ctx.axis;
        const result = fallback.run(anchor, target, { targetScale, subRun: true, ownerWindow });
        if (fitsInViewport(result.target, context.viewportMargin, axis, ownerWindow)) {
            context.positionArea = fallback.ctx.positionArea;
            context.positionMargin = fallback.ctx.positionMargin;
            return result.target;
        }
    }
}
function stepExec({ fn }, ctx) {
    const { targetStart, target, ownerWindow, context: { viewportMargin }, } = ctx;
    function getAdjustedRect(el) {
        const rect = el.getBoundingClientRect();
        // we haven't moved anything yet, so we can simply do rect - targetStart + target
        return makeSimpleDomRect(target.left + (rect.left - targetStart.left), target.top + (rect.top - targetStart.top), rect.width, rect.height);
    }
    return fn({
        ctx,
        fns: {
            getViewportRect: () => getViewportRect(viewportMargin, ownerWindow),
            getAdjustedRect,
            fitsInViewport: (rect) => fitsInViewport(rect, viewportMargin, undefined, ownerWindow),
            computeViewportOverflow: (rect) => computeViewportOverflow(rect, viewportMargin, ownerWindow),
        },
    });
}
////////////////////////////////////////////////////////////////////////////////////////////////////
function resolvePositionArgs(args) {
    const area = resolvePositionArea(args.area);
    const margin = resolveMarginAxis(args.margin);
    return { ...args, area, margin };
}
////////////////////////////////////////////////////////////////////////////////////////////////////
function stepPosition({ area, axis: axisIn, margin, offsetChild }, { anchor, target, targetScale, context, ownerWindow }) {
    const axis = axisIn ?? context.axis;
    context.positionArea = area;
    context.positionMargin = margin;
    let left = positionGeneric(anchor.left, anchor.right, target.width, anchor.width, area.x, margin.x);
    let top = positionGeneric(anchor.top, anchor.bottom, target.height, anchor.height, area.y, margin.y);
    const stepDiv = ownerWindow.document.createElement('div');
    if (context.debug) {
        stepDiv.style.cssText = `
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    top: ${top}px;
    left: ${left}px;
    width: ${target.width}px;
    height: ${target.height}px;
    outline: 2px dashed hsl(${(8 * 137.5) % 360}, 100%, 50%);
    outline-offset: ${4 * 2}px;
  `;
        stepDiv.innerText = 'position pre-offsetchild';
        ownerWindow.document.getElementById('fpl-positioner-debug').appendChild(stepDiv);
    }
    if (offsetChild) {
        const el = resolveElement(offsetChild.element);
        if (el) {
            const childAxis = offsetChild.axis;
            if (context.debug) {
                // eslint-disable-next-line no-console
                console.log('offsetChild', { top: el.offsetTop, left: el.offsetLeft });
            }
            if (childAxis !== 'y') {
                left -= el.offsetLeft * targetScale;
            }
            if (childAxis !== 'x') {
                top -= el.offsetTop * targetScale;
            }
        }
        else {
            if (context.debug) {
                // eslint-disable-next-line no-console
                console.log('offsetChild: element not found');
            }
        }
    }
    if (axis !== 'y')
        target.left = left;
    if (axis !== 'x')
        target.top = top;
}

export { Positioner };
//# sourceMappingURL=positioner.js.map
